<template>
  <div class="language-changer" ref="rootEl">
    <!-- Gear -->
    <button
      class="gear-btn"
      type="button"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="dropdownId"
      aria-haspopup="menu"
      :aria-label="t('HEADER.LANGUAGE.ariaOpen')"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
    >
      <i class="fa-solid fa-gear"></i>
    </button>

    <!-- Dropdown -->
    <div
      class="dropdown"
      v-show="open"
      role="menu"
      :id="dropdownId"
      @keydown.esc.prevent.stop="close"
    >
      <label class="label" :id="labelId">{{ t('HEADER.LANGUAGE.label') }}</label>

      <!-- Flag Select (custom UI) -->
      <div class="flag-select" :class="{ open: fsOpen }">
        <button
          class="fs-current"
          type="button"
          aria-haspopup="listbox"
          :aria-expanded="fsOpen ? 'true' : 'false'"
          :aria-labelledby="labelId"
          @click="fsOpen = !fsOpen"
        >
          <img v-if="current?.flag" class="flag" :src="current.flag" :alt="current.label" />
          <span class="fs-label">{{ current?.label }}</span>
          <i class="fa-solid" :class="fsOpen ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </button>

        <ul v-show="fsOpen" class="fs-list" role="listbox" :aria-labelledby="labelId">
          <li
            v-for="l in languages"
            :key="l.code"
            role="option"
            :aria-selected="selected === l.code ? 'true' : 'false'"
            :class="{ active: selected === l.code }"
            @click="apply(l.code)"
          >
            <img v-if="l.flag" class="flag" :src="l.flag" :alt="l.label" />
            <span class="fs-label">{{ l.label }}</span>
          </li>
        </ul>

        <!-- Visually hidden native select for SR/fallback -->
        <select
          class="sr-only"
          :aria-labelledby="labelId"
          v-model="selected"
          @change="apply(selected)"
        >
          <option v-for="l in languages" :key="l.code" :value="l.code">
            {{ l.label }}
          </option>
        </select>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { setLocale, type Locale } from '@/i18n'
import { addLocalePrefix } from '@/utils/localeRouting'
import trFlag from '@/assets/flags/tr.png'
import enFlag from '@/assets/flags/en.png'
import esFlag from '@/assets/flags/es.png'
import ruFlag from '@/assets/flags/ru.png'
import plFlag from '@/assets/flags/pl.png'
import deFlag from '@/assets/flags/de.svg'
import roFlag from '@/assets/flags/ro.svg'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()

defineOptions({ name: 'LanguageChanger' })

type Lang = { code: string; label: string; flag?: string }

const props = withDefaults(
  defineProps<{
    langs?: Lang[]
    storageKey?: string
  }>(),
  { storageKey: 'locale' },
)

const { locale } = useI18n({ useScope: 'global' }) // v11

// If parent passes langs without flags, we enrich via this map.
const flagByCode: Record<string, string> = {
  en: enFlag,
  tr: trFlag,
  es: esFlag,
  ru: ruFlag,
  pl: plFlag,
  de: deFlag,
  ro: roFlag,
}

const defaultLangs: Lang[] = [
  { code: 'en', label: 'English', flag: enFlag },
  { code: 'de', label: 'Deutsch', flag: deFlag },
  { code: 'ro', label: 'Română', flag: roFlag },
  { code: 'tr', label: 'Türkçe', flag: trFlag },
  { code: 'es', label: 'Español', flag: esFlag },
  { code: 'ru', label: 'Русский', flag: ruFlag },
  { code: 'pl', label: 'Polski', flag: plFlag },
]

const languages = computed<Lang[]>(() => {
  const base = props.langs?.length ? props.langs : defaultLangs
  return base.map((l) => ({ ...l, flag: l.flag ?? flagByCode[l.code] }))
})

const open = ref(false)
const fsOpen = ref(false) // flag select open
const rootEl = ref<HTMLElement | null>(null)
const dropdownId = `lang-dd-${Math.random().toString(36).slice(2)}`
const labelId = `lang-label-${Math.random().toString(36).slice(2)}`
const selected = ref<string>(locale.value)

const current = computed(() => languages.value.find((l) => l.code === selected.value))

async function apply(code: string) {
  const targetLocale = code as Locale
  await setLocale(targetLocale)
  selected.value = code
  fsOpen.value = false
  open.value = false

  // Update URL to match the selected locale
  const newPath = addLocalePrefix(targetLocale, route.path)

  await router.push({
    path: newPath,
    query: route.query,
    hash: route.hash,
  })
}

function toggle() {
  open.value = !open.value
}
function close() {
  open.value = false
  fsOpen.value = false
}

function onDocClick(e: MouseEvent) {
  if (!open.value) return
  const t = e.target as Node
  if (rootEl.value && (rootEl.value === t || rootEl.value.contains(t))) return
  close()
}

onMounted(async () => {
  const saved = localStorage.getItem(props.storageKey)
  const has = (c?: string | null) => !!c && languages.value.some((l) => l.code === c)
  const initial = has(saved) ? (saved as string) : locale.value

  if (initial !== locale.value) await setLocale(initial as any)
  selected.value = locale.value

  document.addEventListener('click', onDocClick, true)
  document.addEventListener('keydown', (e) => {
    if (open.value && e.key === 'Escape') close()
  })
})

watch(
  () => locale.value,
  (val) => {
    selected.value = val
  },
)

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick, true)
})

defineExpose({ rootEl })
</script>

<style scoped lang="scss">
.language-changer {
  position: relative;
  height: 40px;
  width: 40px;
  border-radius: 6px;
  display: flex;
  align-items: center;

  .gear-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 40px;
    border-radius: 6px;
    background: $elevation1;
    border: 0;
    color: $text-light-100;
    font-size: 16px;
    cursor: pointer;
    transition:
      transform 0.1s ease,
      color 0.2s ease;
  }

  .dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 8px;
    min-width: 240px;
    background: linear-gradient(135deg, #1a1f2e, #242b3d);
  border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    box-shadow:
      0 8px 24px rgba(0, 0, 0, 0.35),
      0 2px 6px rgba(0, 0, 0, 0.25);
    padding: 12px;
    z-index: 3000;

    .label {
      display: block;
      font-size: 12px;
      letter-spacing: 0.02em;
      color: $text-light-100;
      margin-bottom: 8px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .flag-select {
      position: relative;

      .fs-current {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 10px;
        background: linear-gradient(135deg, #1a1f2e, #242b3d);
        color: $text-light-100;
        border: 1px solid rgba(255, 255, 255, 0.12);
        border-radius: 8px;
        padding: 8px 10px;
        font-size: 14px;
        cursor: pointer;
        text-align: left;

        .flag {
          width: 18px;
          height: 12px;
          border-radius: 2px;
          object-fit: cover;
        }

        .fs-label {
          flex: 1;
        }

        i {
          font-size: 12px;
        }
      }

      .fs-list {
        position: absolute;
        top: calc(100% + 6px);
        left: 0;
        width: 100%;
        background:linear-gradient(135deg, #1a1f2e, #242b3d);
        border: 1px solid rgba(255, 255, 255, 0.12);
        border-radius: 8px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
        max-height: 220px;
        overflow: auto;
        padding: 6px 0;

        li {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 10px;
          cursor: pointer;
          color: $text-light-100;

          .flag {
            width: 18px;
            height: 12px;
            border-radius: 2px;
            object-fit: cover;
          }

          &.active,
          &:hover {
            background: rgba(255, 190, 85, 0.08);
          }
        }
      }
    }

    /* Visually hidden native select (screen readers still see it) */
    .sr-only {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    }
  }
}
</style>
